<script lang="ts">
  import { onMount } from 'svelte';
  import { useNavigate } from 'svelte-navigator';
  import { _ } from 'svelte-i18n';
  import Time from 'date-and-time';

  import { AppState, SetIconsBackground } from '../etc/stores';
  import { cdn, formatMoney } from '../etc/utils';

  import { Container } from '../services/container';
  import { Auth } from '../services/auth';
  import Analytics from '../services/analytics';

  import Page from '../components/misc/Page.svelte';
  import Button from '../components/form/Button.svelte';
  import Link from '../components/misc/Link.svelte';
  import Image from '../components/misc/Image.svelte';
  import Heading from '../components/typography/Heading.svelte';
  import Text from '../components/typography/Text.svelte';
  import { replaceVariables } from '../i18n';
  import {
    AUTH_STORAGE_FLAGS,
    SessionManager
  } from '../services/session-manager';
  import AuthConnector from '../components/misc/AuthConnector.svelte';

  let clicked_join = false;

  let handleConnectCustomer;

  let guest = $AppState.website.flowGuestSignIn;
  let category = 'guest-state';
  const businessName = $AppState.business.name;

  const navigate = useNavigate();

  const auth = Container.get(Auth);

  const session = Container.get(SessionManager);

  const logo = $AppState.business.logo?.publicPath.large;

  const t = (text: string, values: any = {}) => {
    return replaceVariables($_(`customer-acquisition.${text}`), values);
  };

  const onJoin = async () => {
    onGAEventClickJoin();

    if (await auth.isAuthenticatedOnWebsite()) {
      handleConnectCustomer();
    } else if (auth.canOpenLoginModal()) {
      session.set(AUTH_STORAGE_FLAGS.customerAcquisition, true, {
        expires: Time.addHours(new Date(), 1)
      });
      auth.openLoginModal({ mode: isLoginFlow ? 'login' : 'signup' });
    }
  };

  const onBeforeConnectCustomer = async () => {
    if (!$AppState.auth?.providerData?.email && $AppState.auth.connected) {
      await auth.loadState();
    }
  };

  const onAfterConnectCustomer = () => {
    onGAEventJoinSuccess();
  };

  $: isLoginFlow = !guest;

  $: category = guest ? 'guest-state' : 'join-state';

  const onGAEventJoinSuccess = () => {
    if (clicked_join) Analytics.logEvent('click-join-success', category);
    clicked_join = false;
  };

  const onGAEventClickJoin = () => {
    if (!clicked_join) Analytics.logEvent('click-join', category);
    clicked_join = true;
  };

  const onGAEventClickHowItWorks = () => {
    Analytics.logEvent('click-how-it-works', category);
  };

  const goToHowItWorks = () => {
    onGAEventClickHowItWorks();
    navigate('/how-it-works');
  };

  const triggerConnectCustomer = () => {
    onGAEventClickJoin();
    handleConnectCustomer();
  };

  onMount(() => {
    if ($AppState.website.flow && $AppState.auth.connected) {
      navigate($AppState.website.flow);
    }

    Analytics.logPageView('Customer Acquisition', '/customer-acquisition');

    SetIconsBackground('themed');
  });
</script>

{#if $AppState.business}
  <AuthConnector
    {onAfterConnectCustomer}
    {onBeforeConnectCustomer}
    {triggerConnectCustomer}
    loggerContext="CustomerAcquisition.svelte"
    let:loading
    bind:handleConnectCustomer
  >
    <div class="twism-acquisition">
      <img
        class="twism-acquisition__ilustra"
        alt="Coins ilustration"
        src={cdn('/images/welcome-ilustra.png')}
      />
      <Page className="twism-acquisition__container" padding="0 24px 16px">
        <div
          class="twism-acquisition__coin"
          style={logo
            ? `background-image: url('${process.env.BASE_URL}/images/welcome-coin-background.png');`
            : ''}
        >
          {#if logo}
            <div class="twism-acquisition__coin-logo">
              <Image src={logo} round={true} />
            </div>
          {:else}
            <Image src={`${process.env.BASE_URL}/images/welcome-coin.svg`} />
          {/if}
        </div>
        <div class="twism-acquisition__title">
          <Heading
            type="h2"
            component="h2"
            textAlign="center"
            class="twism-acquisition__title-text"
          >
            {t('title')}
          </Heading>
          <Text
            type="medium"
            textAlign="center"
            class="twism-acquisition__title-message"
          >
            {@html t('description', {
              currency: `${formatMoney($AppState.business.location, 1)} ${
                $AppState.business.location.currency
              }`
            })}
          </Text>
        </div>
        {#if $AppState.business.loyaltyEnabled}
          <div
            class="twism-acquisition__loyalty"
            style="background: {`${$AppState.theme.colors.illustration.secondary}1a`}"
          >
            <Text
              type="small"
              color="#3D3D3D"
              class="twism-acquisition__loyalty-label"
              textAlign="center"
            >
              {t('loyalty', {
                LOYALTY_PERCENTAGE: Number(
                  $AppState.business.rewardsSettings.coinRewardPercentage
                )
              })}
            </Text>
          </div>
        {/if}
        {#if $AppState.auth.providerData || auth.canOpenLoginModal()}
          <div class="twism-acquisition__button">
            <Button label={t('sign_up')} on:click={onJoin} {loading} />
            <div class="twism-acquisition__button-link">
              <Link on:click={goToHowItWorks}>{$_('common.how_it_works')}</Link>
            </div>
          </div>
        {/if}
        <div class="twism-acquisition__footer">
          <Text component="p" type="small" textAlign="center">
            {@html replaceVariables($_('common.footer'), { businessName })}
          </Text>
        </div>
      </Page>
    </div>
  </AuthConnector>
{/if}

<style lang="scss">
  .twism-acquisition {
    background-image: var(--twism-theme-slightGradient);
    background-size: 100% 100%;
    background-repeat: no-repeat;
    height: 100%;
    overflow: hidden;
    &__ilustra {
      width: 100%;
      height: 136px;
    }

    :global(.twism-acquisition__container) {
      height: calc(100% - 120px);
      justify-content: center;
      @media screen and (max-height: 800px) {
        height: calc(100% - 80px);
      }

      @media screen and (max-height: 600px) {
        height: calc(100% - 60px);
      }
    }

    &__loyalty {
      margin: 48px 0;
      padding: 4px 8px;
      border-radius: 6px;
    }

    &__coin {
      margin-top: -178px;
      width: 96px;
      height: 96px;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;
      padding-bottom: 6px;
      padding-left: 2px;
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;

      &-logo {
        border-radius: 50%;
        overflow: hidden;
        width: 72px;
        height: 72px;
      }
    }

    &__title {
      margin-top: 48px;
      padding: 0 6px;
      outline: none !important;
      font-weight: 600;

      :global(.twism-acquisition__title-text) {
        margin: 0 auto 16px;
        max-width: 276px;
        display: flex;
        flex-direction: column;
      }

      :global(.twism-acquisition__title-message) {
        opacity: 0.7;
        max-width: 276px;
      }

      :global(.twism-acquisition__title-message > span) {
        display: block;
        margin: 16px 0;
        font-weight: 700;
      }
    }

    &__button {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;

      &-link {
        margin-top: 24px;
        width: 100%;
      }
    }

    &__footer {
      opacity: 0.5;
      position: absolute;
      bottom: 16px;
      width: calc(100% - 48px);
    }
  }
</style>
